// Copyright (c) Ping. All rights reserved.
//
// One-shot post-signup recovery-phrase prompt. Mounted by `App.tsx`
// between session-bootstrap and AppShell when:
//   * The user just completed signup on this device, AND
//   * No backup-completed marker is set in the local KV store.
//
// V3 mnemonic flow: the host generates a 12-word BIP39 phrase via
// `MessagingClient.generateMnemonicPhrase()`, shows it to the user, and
// — once they confirm they've written it down — builds the V3 recovery
// blob (`buildRecoveryBlobV3`) and uploads it to /v1/recovery/blobs.
//
// On success or skip, the host stamps the local marker so the screen is
// never re-shown on this device.

import { useEffect, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';

import { MessagingClient } from 'ping-openmls-sdk';

import {
  AuthHeader,
  AuthLayout,
  postRecoveryBlob,
  RecoveryMnemonicSetupScreen,
} from '@ping/ui';

import { log } from '@core/log/log';
import { db } from '@core/persistence/db';

import { buildRecoveryBlobV3 } from './recoveryBackupV3';

export const RECOVERY_BACKUP_KV_KEY = 'recovery.backupStatus';

/**
 * Versioned recovery payload status. The blob itself is now the V3
 * mnemonic envelope (SDK Argon2id backup + HKDF-wrapped auth companion);
 * this marker only records that an upload happened locally so App.tsx
 * doesn't re-prompt and Settings can show "backed up".
 */
export type RecoveryBackupStatus =
  | { kind: 'completed'; uploadedAt: number; bytes: number }
  | { kind: 'skipped'; skippedAt: number };

export interface RecoveryBackupSetupHostProps {
  readonly onDone: () => void;
  /**
   * When `true`, the host hides the "Set up later" skip affordance
   * — used for fresh-signup arrivals where the BE's
   * `RequireBackedUp` middleware will 403 every other request
   * until the first backup lands. Defaults to `false` (legacy
   * "rotate backup" path from Settings still allows skip).
   */
  readonly mandatory?: boolean;
}

export function RecoveryBackupSetupHost({
  onDone,
  mandatory = false,
}: RecoveryBackupSetupHostProps): JSX.Element {
  // Generate the mnemonic once on mount. The SDK call is async, so we
  // hold it in state and render a brief loader until it resolves.
  const [mnemonic, setMnemonic] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const phrase = await MessagingClient.generateMnemonicPhrase();
        if (!cancelled) setMnemonic(phrase);
      } catch (err) {
        if (!cancelled) {
          setGenError((err as Error).message);
          log.warn({
            event: 'recovery.signup.mnemonicGenerateFailed',
            message: (err as Error).message,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleComplete = async (): Promise<void> => {
    if (mnemonic === null) {
      throw new Error('Recovery phrase is still loading. Try again in a moment.');
    }
    // Build the V3 blob (SDK Argon2id encryptBackup of identity +
    // DeviceGroup, plus the HKDF-wrapped auth-layer companion) and
    // upload it. Plaintext never leaves this device.
    const ciphertext = await buildRecoveryBlobV3(mnemonic);
    await postRecoveryBlob({ ciphertext });
    await markBackupStatus({
      kind: 'completed',
      uploadedAt: Date.now(),
      bytes: ciphertext.length,
    });
    log.info({ event: 'recovery.signup.uploaded', bytes: ciphertext.length });
    onDone();
  };

  const handleSkip = (): void => {
    void markBackupStatus({ kind: 'skipped', skippedAt: Date.now() })
      .catch((err) => {
        // Marker write failure is non-fatal — the screen still
        // dismisses so the user isn't stuck.
        log.warn({
          event: 'recovery.signup.markerSkippedFailed',
          message: (err as Error).message,
        });
      })
      .finally(() => {
        log.info({ event: 'recovery.signup.skipped' });
        onDone();
      });
  };

  const handleCopy = async (text: string): Promise<void> => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return;
    }
    // Headless / non-secure-context fallback.
    throw new Error('Clipboard not available — copy manually.');
  };

  // The mnemonic is generated async; until it resolves, show a minimal
  // loader so the setup screen never renders with an empty grid.
  if (mnemonic === null) {
    return (
      <AuthLayout accessibilityLabel="recovery-setup-loading">
        <AuthHeader
          title="Save your recovery phrase"
          subtitle="Generating your recovery phrase…"
        />
        <View className="items-center py-xl">
          {genError != null ? (
            <Text className="font-sans text-[14px] leading-[20px] text-light-accent-rose dark:text-dark-accent-rose">
              Couldn't generate a recovery phrase: {genError}
            </Text>
          ) : (
            <ActivityIndicator />
          )}
        </View>
      </AuthLayout>
    );
  }

  return (
    <RecoveryMnemonicSetupScreen
      mnemonic={mnemonic}
      onComplete={handleComplete}
      {...(mandatory ? {} : { onSkip: handleSkip })}
      onCopyToClipboard={handleCopy}
    />
  );
}

/**
 * Reads the local marker that says whether the user has already
 * been prompted (and either completed or skipped) the recovery
 * backup flow on this device. App.tsx calls this once before
 * deciding whether to gate AppShell behind the prompt.
 */
export async function readRecoveryBackupStatus(): Promise<RecoveryBackupStatus | null> {
  await db.open();
  const row = await db.settings_kv.get(RECOVERY_BACKUP_KV_KEY);
  if (!row) return null;
  const value = row.value as RecoveryBackupStatus | undefined;
  if (!value || typeof value !== 'object' || !('kind' in value)) return null;
  return value;
}

async function markBackupStatus(status: RecoveryBackupStatus): Promise<void> {
  await db.open();
  await db.settings_kv.put({
    key: RECOVERY_BACKUP_KV_KEY,
    value: status,
    updatedAt: Date.now(),
  });
}

// First-run recovery popover guide marker. Separate from
// `recovery.backupStatus` so a user who already completed/skipped the
// backup never sees the guide, and a user who saw the guide isn't
// re-nagged.
export const RECOVERY_GUIDE_KV_KEY = 'recovery.guideSeen';

export async function readRecoveryGuideSeen(): Promise<boolean> {
  await db.open();
  const row = await db.settings_kv.get(RECOVERY_GUIDE_KV_KEY);
  return Boolean(row?.value);
}

export async function markRecoveryGuideSeen(): Promise<void> {
  await db.open();
  await db.settings_kv.put({
    key: RECOVERY_GUIDE_KV_KEY,
    value: { seenAt: Date.now() },
    updatedAt: Date.now(),
  });
}
